import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionLogs } from '../../Services/User/SubscriptionPlans';
import './PremiumBadge.css';

/**
 * PremiumBadge Component
 * Displays a premium badge for users with Premium subscription
 * 
 * @param {Object} user - User object that may contain subscription information
 * @param {Object} subscriptionData - Optional subscription logs data to check premium status
 * @param {boolean} fetchSubscription - Whether to fetch subscription logs (default: true for own profile)
 * @param {string} className - Additional CSS classes
 * @param {string} variant - Badge variant ('default' or 'small')
 */
const PremiumBadge = ({ user, subscriptionData, fetchSubscription = false, className = '', variant = 'default' }) => {
  // Fetch subscription logs if needed
  const { data: subscriptionLogs } = useQuery({
    queryKey: ['subscription-logs'],
    queryFn: getSubscriptionLogs,
    enabled: fetchSubscription,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Check if user is premium
  const isPremium = () => {
    if (!user) return false;

    // SEPARATE LOGIC: Profile page vs Favorites API
    // If subscriptionData is provided, use ONLY that (for Profile page)
    if (subscriptionData !== undefined && subscriptionData !== null) {
      // Profile page logic: Check subscription logs API response
      if (subscriptionData?.current_subscription?.subscription_name) {
        const subscriptionName = (subscriptionData.current_subscription.subscription_name || '').toLowerCase();
        return subscriptionName.includes('premium');
      }
      return false;
    }

    // If subscriptionData is NOT provided, use user.subscription (for Favorites API)
    // Check subscription.plan.name from favorites API response
    // Response structure: user.subscription.plan.name (e.g., "12 month Premium Membership")
    // IMPORTANT: If subscription is null, badge should NOT be displayed
    if (user.subscription === null || user.subscription === undefined) {
      // Check fallback fields only if no subscriptionData was provided
      const subscriptionType = (user.subscription_type || '').toLowerCase().trim();
      if (subscriptionType === 'premium') return true;

      const planName = (user.subscription_plan_name || '').toLowerCase().trim();
      if (planName.includes('premium')) return true;

      const subscriptionPlan = (user.subscription_plan || '').toLowerCase().trim();
      if (subscriptionPlan.includes('premium')) return true;

      return false;
    }

    // Favorites API logic: Check user.subscription.plan.name
    // Only show badge if subscription exists, is active, and plan name contains "Premium"
    if (user.subscription && user.subscription.plan && user.subscription.plan.name) {
      // Check if subscription is active
      if (user.subscription.status === 'active') {
        const planName = (user.subscription.plan.name || '').toLowerCase();
        if (planName.includes('premium')) {
          return true;
        }
      }
    }

    return false;
  };

  if (!isPremium()) return null;

  return (
    <span className={`premium-badge ${variant} ${className}`} style={{ display: 'inline-flex' }}>
      <span className="premium-badge-icon">👑</span>
      <span className="premium-badge-text">Premium</span>
    </span>
  );
};

export default PremiumBadge;

